import React, { useState } from 'react';
import { X, Users, User } from 'lucide-react';
import { Button } from '../ui/button';

const GenerateInvoiceModal = ({ isOpen, onClose, onGenerate, parents }) => {
  const [generationType, setGenerationType] = useState('all'); // 'all' or 'single'
  const [formData, setFormData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    parent_id: '',
    platform_charge: 0,
    adjustments: 0,
    discount: 0,
    gst_percentage: 18,
    description: ''
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, show: false });

  if (!isOpen) return null;

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear - 2; i <= currentYear + 1; i++) {
    years.push(i);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsGenerating(true);
    
    const invoiceData = {
      month: parseInt(formData.month),
      year: parseInt(formData.year),
      parent_ids: generationType === 'single' ? [formData.parent_id] : null,
      platform_charge: parseFloat(formData.platform_charge) || 0,
      adjustments: parseFloat(formData.adjustments) || 0,
      discount: parseFloat(formData.discount) || 0,
      gst_percentage: parseFloat(formData.gst_percentage) || 0,
      description: formData.description || undefined
    };

    setProgress({ current: 0, total: 1, show: true });
    
    await onGenerate(invoiceData, (current, total) => {
      setProgress({ current, total, show: true });
    });
    
    setIsGenerating(false);
    setProgress({ current: 0, total: 0, show: false });
    
    setFormData({
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      parent_id: '',
      platform_charge: 0,
      adjustments: 0,
      discount: 0,
      gst_percentage: 18,
      description: ''
    });
    setGenerationType('all');
  };

  const handleClose = () => {
    if (!isGenerating) {
      onClose();
      setProgress({ current: 0, total: 0, show: false });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Generate Invoices</h2>
          <button
            onClick={handleClose}
            disabled={isGenerating}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setGenerationType('all')}
              className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                generationType === 'all'
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-green-300'
              }`}
            >
              <Users className={`w-6 h-6 ${generationType === 'all' ? 'text-green-600' : 'text-gray-500'}`} />
              <span className={`font-semibold ${generationType === 'all' ? 'text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>
                All Parents
              </span>
            </button>
            <button
              type="button"
              onClick={() => setGenerationType('single')}
              className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                generationType === 'single'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-blue-300'
              }`}
            >
              <User className={`w-6 h-6 ${generationType === 'single' ? 'text-blue-600' : 'text-gray-500'}`} />
              <span className={`font-semibold ${generationType === 'single' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                Single Parent
              </span>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Month</label>
              <select
                value={formData.month}
                onChange={(e) => setFormData({...formData, month: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Year</label>
              <select
                value={formData.year}
                onChange={(e) => setFormData({...formData, year: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {generationType === 'single' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Select Parent</label>
              <select
                value={formData.parent_id}
                onChange={(e) => setFormData({...formData, parent_id: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              >
                <option value="">Choose a parent...</option>
                {parents.map(parent => (
                  <option key={parent.id} value={parent.id}>{parent.full_name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Platform Charge (₹)</label>
              <input
                type="number"
                step="0.01"
                value={formData.platform_charge}
                onChange={(e) => setFormData({...formData, platform_charge: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">GST (%)</label>
              <input
                type="number"
                step="0.01"
                value={formData.gst_percentage}
                onChange={(e) => setFormData({...formData, gst_percentage: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="18"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Adjustments (₹)</label>
              <input
                type="number"
                step="0.01"
                value={formData.adjustments}
                onChange={(e) => setFormData({...formData, adjustments: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Discount (₹)</label>
              <input
                type="number"
                step="0.01"
                value={formData.discount}
                onChange={(e) => setFormData({...formData, discount: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Description (Optional)</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              rows="2"
              placeholder={`Invoice for ${months.find(m => m.value === parseInt(formData.month))?.label} ${formData.year}`}
            />
          </div>

          {progress.show && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-blue-700 dark:text-blue-400">Processing Invoices...</span>
                <span className="text-sm text-blue-600 dark:text-blue-400">{progress.current} / {progress.total}</span>
              </div>
              <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button type="button" onClick={handleClose} variant="outline" className="flex-1" disabled={isGenerating}>Cancel</Button>
            <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50" disabled={isGenerating}>
              {isGenerating ? 'Generating...' : 'Generate Invoices'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GenerateInvoiceModal;
